import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contactsAPI } from '../services/api';
import softphone from '../services/softphone';
import { FiArrowLeft, FiPhone, FiPhoneIncoming, FiPhoneOutgoing, FiPhoneMissed, FiFileText, FiCheckSquare, FiDollarSign, FiSend, FiMail, FiBriefcase } from 'react-icons/fi';
import { formatDateTime, formatDuration } from '../utils/formatters';
import toast from 'react-hot-toast';

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [counts, setCounts] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([contactsAPI.get(id), contactsAPI.timeline(id).catch(() => ({ data: { timeline: [] } }))]);
      setContact(c.data.contact);
      setCounts(c.data.counts || {});
      setTimeline(t.data.timeline || []);
    } catch (e) { toast.error('Kontakt topilmadi'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    try { await contactsAPI.addNote(id, note.trim()); setNote(''); load(); }
    catch { toast.error('Yozuv qo\'shishda xato'); }
  };

  const callContact = () => {
    if (!softphone.isRegistered()) { toast.error('Telefoniya ulanmagan'); return; }
    softphone.triggerCall(contact.phone);
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!contact) return <div className="empty-state"><p>Kontakt topilmadi</p></div>;

  return (
    <div>
      <button className="btn btn-ghost" onClick={() => navigate('/admin/contacts')} style={{ marginBottom: 12 }}>
        <FiArrowLeft size={16} /> Kontaktlar
      </button>

      {/* Profil header */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg,#3b82f6,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, fontWeight: 800 }}>
            {(contact.name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{contact.name}</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4, fontSize: 13, color: 'var(--text-muted)' }}>
              <span style={{ fontFamily: 'monospace' }}>{contact.phone}</span>
              {contact.company && <span><FiBriefcase size={12} /> {contact.company}</span>}
              {contact.email && <span><FiMail size={12} /> {contact.email}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginRight: 12 }}>
            <Count label="Bitimlar" value={counts.deals_open} sub={`/${counts.deals_total || 0}`} />
            <Count label="Ochiq vazifa" value={counts.tasks_open} />
          </div>
          <button className="btn btn-primary" onClick={callContact}><FiPhone size={16} /> Qo'ng'iroq</button>
        </div>
        {contact.notes && <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-primary,#0f172a)', borderRadius: 10, fontSize: 13, color: 'var(--text-secondary)' }}>{contact.notes}</div>}
      </div>

      {/* Yozuv qo'shish */}
      <div className="card" style={{ marginBottom: 18, display: 'flex', gap: 10 }}>
        <input className="input" placeholder="Yozuv qo'shish..." value={note}
          onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={addNote}><FiSend size={15} /></button>
      </div>

      {/* Tarix tasmasi */}
      <div className="card">
        <div className="card-header"><span className="card-title">Tarix</span></div>
        {timeline.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Hozircha hodisa yo'q</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {timeline.map((item, i) => <TimelineRow key={item.kind + item.ref_id + i} item={item} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Count({ label, value, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800 }}>{value ?? 0}<span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>{sub}</span></div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

function TimelineRow({ item }) {
  const meta = describe(item);
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 4px', borderBottom: '1px solid var(--border,#2d3a4f)' }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${meta.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <meta.icon size={16} color={meta.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14 }}>{meta.title}</div>
        {meta.sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{meta.sub}</div>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatDateTime(item.ts)}</div>
    </div>
  );
}

function describe(item) {
  if (item.kind === 'call') {
    const inbound = item.direction === 'inbound';
    const missed = item.status === 'missed' || item.status === 'no_answer';
    return {
      icon: missed ? FiPhoneMissed : inbound ? FiPhoneIncoming : FiPhoneOutgoing,
      color: missed ? '#ef4444' : inbound ? '#3b82f6' : '#10b981',
      title: `${inbound ? 'Kiruvchi' : 'Chiquvchi'} qo'ng'iroq — ${missed ? 'Javobsiz' : 'Javob berildi'}${item.duration ? ` (${formatDuration(item.duration)})` : ''}`,
      sub: item.operator_name ? `Operator: ${item.operator_name}` : null,
    };
  }
  if (item.kind === 'activity') {
    return { icon: FiFileText, color: '#8b5cf6', title: item.body || item.type, sub: item.author ? `— ${item.author}` : null };
  }
  if (item.kind === 'task') {
    return { icon: FiCheckSquare, color: item.status === 'done' ? '#10b981' : '#f59e0b',
      title: `Vazifa: ${item.title}`, sub: `${item.status === 'done' ? 'Bajarilgan' : 'Ochiq'}${item.due_at ? ' · muddat ' + formatDateTime(item.due_at) : ''}` };
  }
  if (item.kind === 'deal') {
    return { icon: FiDollarSign, color: '#10b981', title: `Bitim: ${item.title}`,
      sub: `${item.status}${Number(item.amount) ? ' · ' + Number(item.amount).toLocaleString('uz-UZ') + ' ' + (item.currency || '') : ''}` };
  }
  return { icon: FiFileText, color: '#6b7280', title: item.type || 'Hodisa' };
}
